<script lang="ts">
export default {
  name: 'PaginationController',
};
</script>

<script setup lang="ts">
import { computed, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { ChevronsLeft, ChevronsRight, ChevronLeft, ChevronRight } from '@lucide/vue';
import AppButton from '@/components/v2/AppButton.vue';

const route = useRoute();

const props = withDefaults(
  defineProps<{
    page: number;
    totalSize: number;
    pageSize: number;
  }>(),
  {
    page: 1,
    totalSize: 0,
    pageSize: 25,
  },
);

const emit = defineEmits<{
  refresh: [];
}>();

const paginationMaxButtons = 5;

const needsPagination = computed(() => {
  return totalPages.value > 1;
});

const totalPages = computed(() => {
  return Math.ceil(props.totalSize / props.pageSize);
});

const pages = computed(() => {
  const buttonCount = 5;
  if (totalPages.value <= buttonCount) {
    return Array.from(Array(buttonCount).keys()).map((n) => n + 1);
  }
  if (props.page >= buttonCount) {
    const surround = Math.floor(buttonCount / 2);
    const clamp = (n: number): number => {
      if (n < 1) {
        return 1;
      }
      if (n > totalPages.value - buttonCount + 1) {
        return totalPages.value - buttonCount + 1;
      }
      return n;
    };
    const start = clamp(props.page - surround);
    return Array.from(Array(buttonCount).keys()).map((n) => start + n);
  }
  return Array.from(Array(buttonCount).keys()).map((n) => n + 1);
});

const showFirstPage = computed(() => {
  return totalPages.value > paginationMaxButtons;
});

const showLastPage = computed(() => {
  return totalPages.value > paginationMaxButtons;
});

const showPrevPage = computed(() => {
  return totalPages.value > paginationMaxButtons;
});

const showNextPage = computed(() => {
  return totalPages.value > paginationMaxButtons;
});

const hasPrevPage = computed(() => {
  return props.page > 1;
});

const hasNextPage = computed(() => {
  return props.page < totalPages.value;
});

function goToPage(page: number) {
  if (props.page === page) {
    return;
  }
  const path = route.path;
  const query = { ...route.query, page: '' + page };
  useRouter().replace({ path, query });
}

watch(route, (to, from) => {
  if (to.query.page != from.query.page) {
    emit('refresh');
  }
});

function firstPage() {
  goToPage(1);
}

function lastPage() {
  goToPage(totalPages.value);
}

function nextPage() {
  goToPage(props.page + 1);
}

function prevPage() {
  goToPage(props.page - 1);
}
</script>

<template>
  <div
    class="mt-8 text-center"
    v-if="needsPagination"
    data-test="pagination-controller">
    <p>Page {{ page }} of {{ totalPages }}</p>
    <AppButton
      v-if="showFirstPage"
      class="mr-1"
      :disabled="!hasPrevPage"
      data-test="firstPage"
      @click="firstPage">
      <ChevronsLeft :size="16" />
    </AppButton>
    <AppButton
      v-if="showPrevPage"
      class="mr-1"
      :disabled="!hasPrevPage"
      data-test="prevPage"
      @click="prevPage">
      <ChevronLeft :size="16" />
    </AppButton>
    <AppButton
      v-for="pageNumber in pages"
      :key="pageNumber"
      class="mr-1"
      :primary="pageNumber === page"
      :data-test="`page-${pageNumber}`"
      @click="goToPage(pageNumber)">
      {{ pageNumber }}
    </AppButton>
    <AppButton
      v-if="showNextPage"
      class="mr-1"
      :disabled="!hasNextPage"
      data-test="nextPage"
      @click="nextPage">
      <ChevronRight :size="16" />
    </AppButton>
    <AppButton
      v-if="showLastPage"
      :disabled="!hasNextPage"
      data-test="lastPage"
      @click="lastPage">
      <ChevronsRight :size="16" />
    </AppButton>
  </div>
</template>
